import {
  ArrowLeft,
  Building2,
  Button,
  Calendar,
  CreditCard,
  Edit,
  Mail,
  MapPin,
  Phone,
  ToggleRight,
  User,
  UserCheck,
  formatPhoneNumberForDisplay,
} from "@ews-admin/global-design-system";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { LoadingSpinner } from "../components/atoms/LoadingSpinner/LoadingSpinner";
// Shared helper: returns a dash for an absent value and avoids the
// date-only timezone shift. The page used to format an empty string,
// which rendered "Invalid Date" (MED-1423).
import { formatDateTime } from "../utils/date-format-utils";
import { facilityTypeLabel } from "../utils/enum-label-utils";
import { ConfirmationModal } from "../components/molecules/ConfirmationModal/ConfirmationModal";
import { EditManagerModal } from "../components/molecules/EditManagerModal/EditManagerModal";
import { Modal } from "../components/molecules/Modal/Modal";
import { StatusBadge } from "../components/molecules/StatusBadge/StatusBadge";
import { invoiceApi } from "../services/invoice.api";
import managerApi from "../services/manager.api";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  clearError,
  fetchManagerByIdRequest,
  selectManagers,
  selectManagersError,
  selectManagersLoading,
  selectSelectedManager,
  setSelectedManager,
} from "../store/reducers/managersSlice";
import { CivilityEnum, NotificationPrefEnum } from "../types/user.definition";

interface ManagerDetailPageProps {
  managerId?: string;
}

export const ManagerDetailPage: React.FC<ManagerDetailPageProps> = ({
  managerId: propManagerId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { managerId: paramManagerId } = useParams<{ managerId: string }>();

  // Redux state
  const managers = useAppSelector(selectManagers);
  const selectedManager = useAppSelector(selectSelectedManager);
  const loading = useAppSelector(selectManagersLoading);
  const error = useAppSelector(selectManagersError);

  // Local state
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showSendInvoiceModal, setShowSendInvoiceModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [isSendingInvoice, setIsSendingInvoice] = useState(false);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);

  // Use prop managerId if provided, otherwise use URL param
  const managerId = propManagerId || paramManagerId;

  // Get manager from cache or fetch if needed
  const manager =
    selectedManager || managers.find(m => String(m.id) === managerId);

  // MED-1472: a disabled manager account is soft-disabled, never erased, so the same
  // control re-enables it. `disabled` is absent until the gateway flattens it out of
  // the API's nested user, and an absent flag reads as "enabled".
  const isDisabled = manager?.disabled === true;

  // A manager may have several addresses, or none at all. Prefer the primary one.
  const primaryLocation =
    manager?.locations?.find(location => location.isPrimary) ??
    manager?.locations?.[0];

  // Fetch manager data if not in cache
  useEffect(() => {
    if (!managerId) {
      return;
    }

    // Check if we already have this manager in cache
    const cachedManager = managers.find(m => String(m.id) === managerId);

    if (cachedManager) {
      dispatch(setSelectedManager(cachedManager));
      return;
    }

    // If not in cache, fetch from API
    if (managerId) {
      dispatch(fetchManagerByIdRequest(managerId));
    }
  }, [managerId, managers, dispatch]);

  // Clear error when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleEditManagerSuccess = () => {
    setShowEditModal(false);
    if (managerId) {
      dispatch(fetchManagerByIdRequest(managerId));
    }
  };

  const handleSendInvoice = () => {
    setShowSendInvoiceModal(true);
  };

  // MED-1471: the real two-step send — find the facility's outstanding invoice, then
  // send it. This used to be a console.log, so the confirmation always "succeeded".
  const handleSendInvoiceConfirm = async () => {
    if (!manager?.healthcareFacility) return;

    setIsSendingInvoice(true);
    try {
      const invoice = await invoiceApi.getPendingForFacility(
        manager.healthcareFacility.id
      );
      await invoiceApi.send(invoice.id);
      toast.success(t("admin.managers.toast.invoiceSent"));
      setShowSendInvoiceModal(false);
    } catch (error) {
      // The two refusals the admin can act on, told apart by their API error code:
      // INVOICE_NOT_FOUND (404) means the facility owes nothing — a TRIAL licence,
      // typically — and INVALID_PARAMETER (400) that no e-mail recipient is on file.
      const code = (error as { code?: string } | null)?.code;
      if (code === "INVOICE_NOT_FOUND") {
        toast.error(t("admin.managers.toast.invoiceNoPending"));
      } else if (code === "INVALID_PARAMETER") {
        toast.error(t("admin.managers.toast.invoiceNoRecipient"));
      } else {
        toast.error(t("admin.managers.toast.invoiceError"));
      }
    } finally {
      setIsSendingInvoice(false);
    }
  };

  const handleSendInvoiceCancel = () => {
    setShowSendInvoiceModal(false);
  };

  const handleToggleStatus = () => {
    setShowDisableModal(true);
  };

  // MED-1472: DELETE /managers/{id} disables (a soft disable — nothing is erased),
  // PUT /managers/{id}/restore re-enables. Both used to be a console.log. The badge is
  // refreshed from the server afterwards rather than flipped optimistically.
  const handleToggleStatusConfirm = async () => {
    if (!manager) return;

    setIsTogglingStatus(true);
    try {
      if (isDisabled) {
        await managerApi.restoreManager(manager.id);
      } else {
        await managerApi.disableManager(manager.id);
      }
      if (managerId) {
        dispatch(fetchManagerByIdRequest(managerId));
      }
      toast.success(
        t(
          isDisabled
            ? "admin.managers.toast.enableSuccess"
            : "admin.managers.toast.disableSuccess"
        )
      );
      setShowDisableModal(false);
    } catch {
      toast.error(
        t(
          isDisabled
            ? "admin.managers.toast.enableError"
            : "admin.managers.toast.disableError"
        )
      );
    } finally {
      setIsTogglingStatus(false);
    }
  };

  const handleToggleStatusCancel = () => {
    setShowDisableModal(false);
  };

  const handleContact = () => {
    setShowContactModal(true);
  };

  const handleContactCancel = () => {
    setShowContactModal(false);
  };

  const getCivilityLabel = (civility: string) => {
    switch (civility) {
      case CivilityEnum.MR:
        return t("common.mr");
      case CivilityEnum.MRS:
        return t("common.mrs");

      default:
        return civility;
    }
  };

  const getNotificationMethodLabel = (method: string) => {
    switch (method) {
      case NotificationPrefEnum.EMAIL:
        return t("common.email");
      case NotificationPrefEnum.SMS:
        return t("common.sms");
      default:
        return method;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-gray-600">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate("/admin/managers")}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="w-6 h-6" />
              <span>{t("common.back")}</span>
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t("admin.managers.error")}
              </h1>
              <p className="text-gray-600">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Manager not found
  if (!manager) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate("/admin/managers")}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="w-6 h-6" />
              <span>{t("common.back")}</span>
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t("admin.managers.notFound")}
              </h1>
              <p className="text-gray-600">
                {t("admin.managers.notFoundDescription")}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="flex justify-between items-center page-header">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate("/admin/managers")}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-6 h-6" />
            <span>{t("common.back")}</span>
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {getCivilityLabel(manager.civility)} {manager.firstName}{" "}
              {manager.lastName}
            </h1>
            <p className="text-gray-600">
              {t("admin.managers.detailSubtitle")}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={handleContact}
            className="flex items-center space-x-2"
          >
            <Mail className="w-6 h-6" />
            <span>{t("admin.managers.contact")}</span>
          </Button>
          <Button
            variant="outline"
            onClick={handleSendInvoice}
            // Nothing to bill without a facility: the invoice belongs to the licence.
            disabled={!manager.healthcareFacility}
            className="flex items-center space-x-2"
          >
            <CreditCard className="w-6 h-6" />
            <span>{t("common.sendInvoice")}</span>
          </Button>

          <Button
            variant="outline"
            onClick={handleToggleStatus}
            className="flex items-center space-x-2"
          >
            <ToggleRight className="w-6 h-6" />
            <span>{t(isDisabled ? "common.enable" : "common.disable")}</span>
          </Button>
          <Button
            onClick={() => setShowEditModal(true)}
            className="flex items-center space-x-2"
          >
            <Edit className="w-6 h-6" />
            <span>{t("common.edit")}</span>
          </Button>
        </div>
      </div>

      {/* Manager Overview */}
      <div className="overflow-hidden bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="flex">
          {/* Manager Photo */}
          <div className="flex flex-shrink-0 justify-center items-center w-64 h-64 bg-gray-100">
            {manager.photoUrl ? (
              <img
                src={manager.photoUrl}
                alt={`${manager.firstName} ${manager.lastName}`}
                className="object-cover w-full h-full"
              />
            ) : (
              <div className="flex justify-center items-center w-full h-full bg-gradient-to-br from-blue-50 to-blue-100">
                <User className="w-16 h-16 text-blue-400" />
              </div>
            )}
          </div>

          {/* Manager Details */}
          <div className="flex-1 p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="flex items-center mb-2 space-x-3">
                  <span className="inline-flex items-center px-3 py-1 text-sm font-medium text-blue-800 bg-blue-100 rounded-full">
                    <UserCheck className="mr-2 w-4 h-4" />
                    {manager.roleName}
                  </span>
                  <StatusBadge
                    status={t(
                      isDisabled
                        ? "common.statuses.inactive"
                        : "common.statuses.active"
                    )}
                  />
                </div>
                <h2 className="mb-2 text-2xl font-bold text-gray-900">
                  {getCivilityLabel(manager.civility)} {manager.firstName}{" "}
                  {manager.lastName}
                </h2>
                <p className="text-gray-600">{manager.email}</p>
              </div>
            </div>

            {/* Contact Information */}
            <div className="grid grid-cols-1 gap-4 mb-6 md:grid-cols-2">
              {primaryLocation && (
                <div className="flex items-center text-gray-600">
                  <MapPin className="flex-shrink-0 mr-3 w-5 h-5" />
                  <div>
                    <div className="font-medium">{primaryLocation.address}</div>
                    <div className="text-sm">
                      {[
                        primaryLocation.city,
                        primaryLocation.state,
                        primaryLocation.zip,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </div>
                  </div>
                </div>
              )}
              <div className="flex items-center text-gray-600">
                <Phone className="flex-shrink-0 mr-3 w-5 h-5" />
                <span>{formatPhoneNumberForDisplay(manager.phoneNumber)}</span>
              </div>
              <div className="flex items-center text-gray-600">
                <Mail className="flex-shrink-0 mr-3 w-5 h-5" />
                <span>{manager.email}</span>
              </div>
              <div className="flex items-center text-gray-600">
                <Calendar className="flex-shrink-0 mr-3 w-5 h-5" />
                <span>{formatDateTime(manager.dateOfBirth)}</span>
              </div>
            </div>

            {/* Notification Preferences */}
            <div className="mb-6">
              <h3 className="mb-3 text-lg font-semibold text-gray-900">
                {t("admin.managers.notificationPreferences")}
              </h3>
              <div className="flex flex-wrap gap-2">
                {manager.preferredNotificationMethods?.map((method, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 text-sm text-green-700 bg-green-100 rounded-full"
                  >
                    {getNotificationMethodLabel(method)}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Assigned Facility */}
      {manager.healthcareFacility && (
        <div className="page-section card-container">
          <h3 className="mb-4 text-lg font-semibold text-gray-900">
            {t("admin.managers.assignedFacility")}
          </h3>
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              <div className="flex justify-center items-center w-16 h-16 bg-gray-100 rounded-lg">
                <Building2 className="w-8 h-8 text-gray-400" />
              </div>
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-medium text-gray-900">
                {manager.healthcareFacility.name}
              </h4>
              <p className="text-gray-600">
                {facilityTypeLabel(t, manager.healthcareFacility.facilityType)}
              </p>
              <p className="text-sm text-gray-500">
                {manager.healthcareFacility.location.address},{" "}
                {manager.healthcareFacility.location.city}
              </p>
            </div>
            <div className="flex-shrink-0">
              <Button
                variant="outline"
                onClick={() =>
                  navigate(
                    `/admin/facilities/${manager.healthcareFacility?.id}`
                  )
                }
                className="flex items-center space-x-2"
              >
                <Building2 className="w-6 h-6" />
                <span>{t("common.view")}</span>
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showEditModal && (
        <EditManagerModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          manager={manager}
          onSuccess={handleEditManagerSuccess}
        />
      )}

      {showAssignModal && (
        <Modal
          isOpen={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          title={t("admin.managers.assignFacility")}
        >
          <div className="p-6">
            <p className="text-gray-600">
              {t("admin.managers.assignFacilityContent", {
                name: `${manager.firstName} ${manager.lastName}`,
              })}
            </p>
            <div className="flex justify-end mt-6 space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowAssignModal(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button onClick={() => setShowAssignModal(false)}>
                {t("admin.managers.assign")}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {showSettingsModal && (
        <Modal
          isOpen={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
          title={t("admin.managers.settings")}
        >
          <div className="p-6">
            <p className="text-gray-600">
              {t("admin.managers.settingsContent", {
                name: `${manager.firstName} ${manager.lastName}`,
              })}
            </p>
            <div className="flex justify-end mt-6 space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowSettingsModal(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button onClick={() => setShowSettingsModal(false)}>
                {t("common.save")}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Send Invoice Confirmation Modal */}
      <ConfirmationModal
        isOpen={showSendInvoiceModal}
        onClose={handleSendInvoiceCancel}
        onConfirm={handleSendInvoiceConfirm}
        title={t("common.sendInvoice")}
        message={t("common.confirmations.sendInvoiceMessage", {
          name: manager?.firstName,
        })}
        confirmLabel={t("common.send")}
        cancelLabel={t("common.cancel")}
        variant="info"
        isLoading={isSendingInvoice}
      />

      {/* Disable Manager Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDisableModal}
        onClose={handleToggleStatusCancel}
        onConfirm={handleToggleStatusConfirm}
        title={t(
          isDisabled
            ? "common.confirmations.enableManager"
            : "common.confirmations.disableManager"
        )}
        message={t(
          isDisabled
            ? "common.confirmations.enableManagerMessage"
            : "common.confirmations.disableManagerMessage"
        )}
        confirmLabel={t(isDisabled ? "common.enable" : "common.disable")}
        cancelLabel={t("common.cancel")}
        variant="warning"
        isLoading={isTogglingStatus}
      />

      {/* Contact Modal */}
      <Modal
        isOpen={showContactModal}
        onClose={handleContactCancel}
        title={t("admin.managers.contact")}
      >
        <div className="p-6">
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Mail className="w-5 h-5 text-ews-primary" />
              <div>
                <p className="text-sm font-medium text-gray-900">Email</p>
                <p className="text-sm text-gray-600">{manager?.email}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Phone className="w-5 h-5 text-ews-primary" />
              <div>
                <p className="text-sm font-medium text-gray-900">Téléphone</p>
                <p className="text-sm text-gray-600">
                  {manager?.phoneNumber
                    ? formatPhoneNumberForDisplay(manager.phoneNumber)
                    : ""}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <MapPin className="w-5 h-5 text-ews-primary" />
              <div>
                <p className="text-sm font-medium text-gray-900">Adresse</p>
                <p className="text-sm text-gray-600">
                  {primaryLocation
                    ? [primaryLocation.address, primaryLocation.city]
                        .filter(Boolean)
                        .join(", ")
                    : "—"}
                </p>
              </div>
            </div>
          </div>
          <div className="flex justify-end mt-6 space-x-3">
            <Button variant="outline" onClick={handleContactCancel}>
              {t("common.close")}
            </Button>
            <Button onClick={() => window.open(`mailto:${manager?.email}`)}>
              {t("common.sendEmail")}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
